/**
 * CLOCK-IT LOGGING SERVICE
 * 
 * High-performance centralized logging service for React web application
 * Implements comprehensive event tracking with numeric codes for optimal database performance
 */

import { supabase } from '../lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// EVENT CODE CONSTANTS
// ============================================================================
export const EventCode = {
  // Authentication Events (1000-1999)
  LOGIN_ATTEMPT: 1000,
  LOGIN_SUCCESS: 1001,
  LOGIN_FAILED_INVALID_CREDENTIALS: 1002,
  LOGIN_FAILED_ACCOUNT_LOCKED: 1003,
  LOGIN_FAILED_EMAIL_NOT_VERIFIED: 1004,
  
  // OAuth Events (1100-1199)
  OAUTH_INITIATED: 1100,
  OAUTH_SUCCESS: 1101,
  OAUTH_FAILED: 1102,
  OAUTH_CANCELLED: 1103,
  OAUTH_CALLBACK_RECEIVED: 1104,
  OAUTH_REDIRECT_LOOP_DETECTED: 1105,
  
  // Registration Events (1200-1299)
  REGISTRATION_ATTEMPT: 1200,
  REGISTRATION_SUCCESS: 1201,
  REGISTRATION_FAILED_EMAIL_EXISTS: 1202,
  REGISTRATION_FAILED_VALIDATION: 1203,
  
  // Session Events (1300-1399)
  SESSION_CREATED: 1300,
  SESSION_EXPIRED: 1301,
  SESSION_TERMINATED: 1302,
  LOGOUT_SUCCESS: 1303,
  
  // Password Events (1400-1499)
  PASSWORD_RESET_REQUESTED: 1400,
  PASSWORD_RESET_SUCCESS: 1401,
  PASSWORD_RESET_FAILED: 1402,
  PASSWORD_CHANGE_SUCCESS: 1403,
  
  // Onboarding Events (2000-2999)
  ONBOARDING_STARTED: 2000,
  ONBOARDING_STEP_COMPLETED: 2001,
  ONBOARDING_COMPLETED: 2002,
  ONBOARDING_SKIPPED: 2003,
  ONBOARDING_ABANDONED: 2004,
  
  // Profile Events (2100-2199)
  PROFILE_CREATION_STARTED: 2100,
  PROFILE_CREATION_SUCCESS: 2101,
  PROFILE_UPDATE_SUCCESS: 2102,
  AVATAR_UPLOAD_SUCCESS: 2103,
  
  // Privacy Events (3000-3999)
  PRIVACY_POLICY_VIEWED: 3000,
  PRIVACY_POLICY_ACCEPTED: 3001,
  TERMS_OF_SERVICE_VIEWED: 3002,
  TERMS_OF_SERVICE_ACCEPTED: 3003,
  PRIVACY_CONSENT_REQUIRED: 3004,
  PRIVACY_CONSENT_BYPASSED_OAUTH: 3005,
  
  // Error Events (4000-4999)
  APPLICATION_ERROR: 4000,
  DATABASE_ERROR: 4001,
  API_ERROR: 4002,
  VALIDATION_ERROR: 4003,
  NETWORK_ERROR: 4004,
  
  // Authentication System Errors (4100-4199)
  AUTH_SYSTEM_INIT_ERROR: 4100,
  AUTH_PROVIDER_ERROR: 4101,
  AUTH_DATABASE_CONNECTION_ERROR: 4102,
  AUTH_SUPABASE_CONFIG_ERROR: 4103,
  AUTH_TOKEN_VALIDATION_ERROR: 4104,
  AUTH_USER_PROFILE_ERROR: 4105,
  
  // Security Events (5000-5999)
  SUSPICIOUS_LOGIN_ATTEMPT: 5000,
  RATE_LIMIT_EXCEEDED: 5001,
  INVALID_TOKEN: 5002,
  CSRF_ATTACK_DETECTED: 5003,
  
  // Scraper Operation Events (6000-6099)
  SCRAPER_STARTED: 6000,
  SCRAPER_STOPPED: 6001,
  SCRAPER_CRASHED: 6002,
  SCRAPER_HEARTBEAT: 6003,
  SCRAPER_TIMEOUT: 6004,
  SCRAPER_MEMORY_WARNING: 6005,
  
  // Data Parsing Events (6100-6199)
  PARSING_SUCCESS: 6100,
  PARSING_FAILED: 6101,
  PARSING_PARTIAL: 6102,
  PARSING_SUSPICIOUS_CONTENT: 6103,
  PARSING_DUPLICATE_DETECTED: 6104,
  PARSING_FORMAT_CHANGED: 6105,
  
  // Network & HTTP Events (6200-6299)
  REQUEST_SUCCESS: 6200,
  REQUEST_FAILED: 6201,
  REQUEST_TIMEOUT: 6202,
  RATE_LIMIT_HIT: 6203,
  REQUEST_BLOCKED: 6204,
  SSL_ERROR: 6205,
  
  // Deployment & Update Events (6300-6399)
  DEPLOYMENT_STARTED: 6300,
  DEPLOYMENT_SUCCESS: 6301,
  DEPLOYMENT_FAILED: 6302,
  CODE_UPDATE_RECEIVED: 6303,
  CONFIG_UPDATE_RECEIVED: 6304,
  ROLLBACK_INITIATED: 6305,
  VERSION_MISMATCH: 6306,
  
  // AI Analysis Events (6400-6499)
  AI_ANALYSIS_QUEUED: 6400,
  AI_ANALYSIS_COMPLETED: 6401,
  AI_PATTERN_DETECTED: 6402,
  AI_ANALYSIS_FAILED: 6403,
  AI_PARSER_GENERATED: 6404,
  AI_CONFIDENCE_LOW: 6405,
  
  // Security & Authentication Events (6500-6599)
  SCRAPER_AUTH_SUCCESS: 6500,
  SCRAPER_AUTH_FAILED: 6501,
  API_KEY_ROTATED: 6502,
  SUSPICIOUS_ACTIVITY: 6503,
  CONNECTION_ENCRYPTED: 6504,
  UNAUTHORIZED_ACCESS: 6505,
} as const;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================
export type EventCodeType = typeof EventCode[keyof typeof EventCode];

export interface LogEventOptions {
  userId?: string;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
  platform?: 'web' | 'mobile' | 'desktop';
  eventData?: Record<string, any>;
  requestId?: string;
  correlationId?: string;
  errorMessage?: string;
  errorStack?: string;
  durationMs?: number;
  timezone?: string;
}

export interface LogEvent {
  event_code: number;
  user_id?: string;
  session_id?: string;
  ip_address?: string;
  user_agent?: string;
  platform: string;
  event_data: Record<string, any>;
  request_id?: string;
  correlation_id?: string;
  error_message?: string;
  error_stack?: string;
  duration_ms?: number;
  timezone?: string;
  timestamp: string;
}

// ============================================================================
// LOGGING SERVICE CLASS
// ============================================================================
class LoggingService {
  private supabase: SupabaseClient = supabase;
  private sessionId: string;
  private requestQueue: LogEvent[] = [];
  private batchTimer?: NodeJS.Timeout;
  private readonly BATCH_SIZE = 10;
  private readonly BATCH_TIMEOUT = 1000; // 1 second
  private isProcessing = false;

  // Error handling to prevent infinite spam
  private failureCount = 0;
  private lastFailureTime = 0;
  private readonly MAX_FAILURES = 5;
  private readonly FAILURE_COOLDOWN = 60000; // 1 minute
  private isDisabled = false;

  constructor() {
    // Generate session ID for this browser session
    this.sessionId = this.generateSessionId();
  }

  /**
   * Log an event with comprehensive context
   */
  async logEvent(
    eventCode: EventCodeType,
    options: LogEventOptions = {}
  ): Promise<boolean> {
    try {
      const event: LogEvent = {
        event_code: eventCode,
        user_id: options.userId,
        session_id: options.sessionId || this.sessionId,
        ip_address: options.ipAddress || await this.getClientIP(),
        user_agent: options.userAgent || navigator.userAgent,
        platform: options.platform || 'web',
        event_data: options.eventData || {},
        request_id: options.requestId || this.generateRequestId(),
        correlation_id: options.correlationId,
        error_message: options.errorMessage,
        error_stack: options.errorStack,
        duration_ms: options.durationMs,
        timezone: options.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        timestamp: new Date().toISOString(),
      };

      // Add to batch queue
      this.requestQueue.push(event);

      // Process immediately for critical events
      if (this.isCriticalEvent(eventCode)) {
        await this.processBatch();
      } else {
        // Schedule batch processing
        this.scheduleBatchProcessing();
      }

      return true;
    } catch (error) {
      console.error('Logging service error:', error);
      return false;
    }
  }

  /**
   * Log authentication events with consistent context
   */
  async logAuthEvent(
    eventCode: EventCodeType,
    userId?: string,
    additionalData: Record<string, any> = {}
  ): Promise<boolean> {
    return this.logEvent(eventCode, {
      userId,
      eventData: {
        ...additionalData,
        timestamp: Date.now(),
      },
    });
  }

  /**
   * Log onboarding events with step tracking
   */
  async logOnboardingEvent(
    eventCode: EventCodeType,
    userId: string,
    stepData: Record<string, any> = {}
  ): Promise<boolean> {
    return this.logEvent(eventCode, {
      userId,
      eventData: {
        ...stepData,
        onboarding_session: this.sessionId,
      },
    });
  }

  /**
   * Log privacy-related events
   */
  async logPrivacyEvent(
    eventCode: EventCodeType,
    userId?: string,
    privacyData: Record<string, any> = {}
  ): Promise<boolean> {
    return this.logEvent(eventCode, {
      userId,
      eventData: {
        ...privacyData,
        consent_timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Log errors with full context
   */
  async logError(
    eventCode: EventCodeType,
    error: Error,
    userId?: string,
    additionalContext: Record<string, any> = {}
  ): Promise<boolean> {
    return this.logEvent(eventCode, {
      userId,
      errorMessage: error.message,
      errorStack: error.stack,
      eventData: {
        ...additionalContext,
        error_name: error.name,
        error_timestamp: Date.now(),
      },
    });
  }

  /**
   * Performance tracking for timed operations
   */
  startTimer(operationId: string): (eventCode: EventCodeType, userId?: string, eventData?: Record<string, any>) => Promise<boolean> {
    const startTime = Date.now();
    
    return async (eventCode: EventCodeType, userId?: string, eventData: Record<string, any> = {}) => {
      const duration = Date.now() - startTime;
      
      return this.logEvent(eventCode, {
        userId,
        durationMs: duration,
        eventData: {
          ...eventData,
          operation_id: operationId,
          start_time: startTime,
        },
      });
    };
  }

  /**
   * Correlation ID generation for request tracing
   */
  generateCorrelationId(): string {
    return `cor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Force immediate batch processing
   */
  async flush(): Promise<void> {
    if (this.requestQueue.length > 0) {
      await this.processBatch();
    }
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================
  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async getClientIP(): Promise<string | undefined> {
    try {
      // In production, this would typically come from headers or a service
      // For now, return undefined and let the server handle it
      return undefined;
    } catch {
      return undefined;
    }
  }

  private isCriticalEvent(eventCode: number): boolean {
    // Critical events that should be logged immediately
    return eventCode >= 5000 || // Security events
           eventCode === EventCode.LOGIN_SUCCESS ||
           eventCode === EventCode.REGISTRATION_SUCCESS ||
           eventCode === EventCode.OAUTH_SUCCESS;
  }

  private scheduleBatchProcessing(): void {
    // Stop processing if disabled due to too many failures
    if (this.isDisabled) {
      const now = Date.now();
      if (now - this.lastFailureTime > this.FAILURE_COOLDOWN) {
        console.log('🔄 Re-enabling logging service after cooldown');
        this.isDisabled = false;
        this.failureCount = 0;
      } else {
        console.log('⏸️ Logging service temporarily disabled due to failures');
        return;
      }
    }

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    this.batchTimer = setTimeout(() => {
      this.processBatch();
    }, this.BATCH_TIMEOUT);

    // Process immediately if batch is full
    if (this.requestQueue.length >= this.BATCH_SIZE) {
      clearTimeout(this.batchTimer);
      this.processBatch();
    }
  }

  private async processBatch(): Promise<void> {
    // Don't process if disabled or already processing
    if (this.isDisabled || this.isProcessing || this.requestQueue.length === 0) {
      if (this.isDisabled) {
        console.log('⏸️ Skipping batch processing - service disabled');
      }
      return;
    }

    this.isProcessing = true;

    try {
      const batch = this.requestQueue.splice(0, this.BATCH_SIZE);
      console.log(`🔄 Processing batch of ${batch.length} log entries`);

      // Insert batch into database using any type to handle Supabase typing issues
      const { error } = await this.supabase
        .from('event_logs')
        .insert(batch as any);

      if (error) {
        console.error('❌ Batch logging error:', error);
        this.handleFailure();
        
        // Don't re-queue on failure to prevent infinite loops
      } else {
        console.log(`✅ Successfully logged ${batch.length} events`);
        // Reset failure count on success
        this.failureCount = 0;
      }
    } catch (error) {
      console.error('❌ Batch processing exception:', error);
      this.handleFailure();
    } finally {
      this.isProcessing = false;
      
      // Process remaining queue if needed and not disabled
      if (this.requestQueue.length > 0 && !this.isDisabled) {
        this.scheduleBatchProcessing();
      }
    }
  }

  private handleFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    console.warn(`⚠️ Logging failure #${this.failureCount}/${this.MAX_FAILURES}`);
    
    if (this.failureCount >= this.MAX_FAILURES) {
      this.isDisabled = true;
      console.error(`🚫 Disabling logging service after ${this.MAX_FAILURES} failures. Will retry after ${this.FAILURE_COOLDOWN/1000} seconds`);
      
      // Clear the queue to prevent memory buildup
      this.requestQueue.length = 0;
      console.log('🗑️ Cleared request queue to prevent memory buildup');
    }
  }
}

// ============================================================================
// SINGLETON INSTANCE - RE-ENABLED (event_logs tables confirmed to exist!)
// ============================================================================
export const loggingService = new LoggingService();

// Previous mock implementation (now removed since tables exist):
// export const loggingService = { 
//   logEvent: () => Promise.resolve(true), 
//   logAuthEvent: () => Promise.resolve(true), 
//   logUserAction: () => Promise.resolve(true),
//   logError: () => Promise.resolve(true),
//   logPerformance: () => Promise.resolve(true),
//   logOnboardingEvent: () => Promise.resolve(true),
//   logPrivacyEvent: () => Promise.resolve(true),
//   startTimer: () => () => Promise.resolve(true),
//   generateCorrelationId: () => 'temp-id',
//   flush: () => Promise.resolve()
// } as any;

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================
export const logEvent = (eventCode: EventCodeType, options?: LogEventOptions) => 
  loggingService.logEvent(eventCode, options);

export const logAuthEvent = (eventCode: EventCodeType, userId?: string, additionalData?: Record<string, any>) =>
  loggingService.logAuthEvent(eventCode, userId, additionalData);

export const logOnboardingEvent = (eventCode: EventCodeType, userId: string, stepData?: Record<string, any>) =>
  loggingService.logOnboardingEvent(eventCode, userId, stepData);

export const logPrivacyEvent = (eventCode: EventCodeType, userId?: string, privacyData?: Record<string, any>) =>
  loggingService.logPrivacyEvent(eventCode, userId, privacyData);

export const logError = (eventCode: EventCodeType, error: Error, userId?: string, context?: Record<string, any>) =>
  loggingService.logError(eventCode, error, userId, context);

// ============================================================================
// REACT HOOK
// ============================================================================
export const useLogging = () => {
  return {
    logEvent: loggingService.logEvent.bind(loggingService),
    logAuthEvent: loggingService.logAuthEvent.bind(loggingService),
    logOnboardingEvent: loggingService.logOnboardingEvent.bind(loggingService),
    logPrivacyEvent: loggingService.logPrivacyEvent.bind(loggingService),
    logError: loggingService.logError.bind(loggingService),
    startTimer: loggingService.startTimer.bind(loggingService),
    generateCorrelationId: loggingService.generateCorrelationId.bind(loggingService),
    flush: loggingService.flush.bind(loggingService),
    EventCode,
  };
};

export default loggingService;